import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { FiChevronDown } from "react-icons/fi";
import CallCenterModal from "./listing/CallCenterModal";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";

const normalizeOfficeUser = (user) => {
  const userId = user?.userId ?? user?.id;
  if (userId == null || userId === "") {
    return null;
  }
  const numericId = Number(userId);
  if (Number.isNaN(numericId)) {
    return null;
  }
  return {
    userId: numericId,
    label: user?.name || user?.email || `#${userId}`,
  };
};

const TicketAssignModal = ({
  open,
  title,
  description,
  officeUsers = [],
  officeUsersLoading = false,
  initialAssigneeId = "",
  allowUnassign = false,
  loading = false,
  confirmLabel,
  unassignLabel,
  cancelLabel,
  selectAgentLabel,
  selectAgentPlaceholder,
  loadingAgentsLabel,
  noAgentsLabel,
  onConfirm,
  onUnassign,
  onClose,
}) => {
  const [assigneeId, setAssigneeId] = useState("");

  useEffect(() => {
    if (!open) return;
    setAssigneeId(initialAssigneeId ? String(initialAssigneeId) : "");
  }, [open, initialAssigneeId]);

  const agentOptions = useMemo(
    () =>
      [...officeUsers]
        .map(normalizeOfficeUser)
        .filter(Boolean)
        .sort((left, right) => left.label.localeCompare(right.label)),
    [officeUsers]
  );

  const selectedAgent =
    agentOptions.find((agent) => String(agent.userId) === String(assigneeId)) ?? null;

  const canSubmit = Boolean(assigneeId);
  const pickerDisabled = loading || officeUsersLoading;

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      blockClose={loading}
      title={title}
      subtitle={description}
      maxWidth="md"
      zIndexClass="z-[260]"
      closeLabel={cancelLabel}
      contentVariant="card"
      footer={
        <>
          {allowUnassign && onUnassign ? (
            <button
              type="button"
              onClick={onUnassign}
              disabled={loading}
              className="mr-auto h-10 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 sm:mr-auto"
            >
              {unassignLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-10 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={!canSubmit || loading || officeUsersLoading}
            onClick={() => onConfirm?.(Number(assigneeId))}
            className={`${LISTING_PRIMARY_BUTTON_CLASS} disabled:opacity-60`}
          >
            {loading ? selectAgentPlaceholder : confirmLabel}
          </button>
        </>
      }
    >
      <div>
        <label className={FILTER_LABEL_CLASS}>{selectAgentLabel}</label>
        <Listbox
          value={selectedAgent}
          onChange={(agent) => setAssigneeId(agent ? String(agent.userId) : "")}
          disabled={pickerDisabled}
        >
          {({ open: listOpen }) => (
            <div className="relative mt-1">
              <Listbox.Button
                className={`${FILTER_FIELD_CLASS} relative flex items-center justify-between pr-10 text-left ${
                  pickerDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                }`}
              >
                <span className={selectedAgent ? "text-slate-700" : "text-slate-400"}>
                  {officeUsersLoading
                    ? loadingAgentsLabel
                    : selectedAgent?.label || selectAgentPlaceholder}
                </span>
                <FiChevronDown
                  className="pointer-events-none absolute right-3 h-4 w-4 text-slate-400"
                  aria-hidden
                />
              </Listbox.Button>
              <Transition
                show={listOpen}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-[300] mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                  {agentOptions.length === 0 ? (
                    <div className="px-3 py-2 text-slate-500">{noAgentsLabel}</div>
                  ) : (
                    agentOptions.map((agent) => (
                      <Listbox.Option
                        key={agent.userId}
                        value={agent}
                        className={({ active }) =>
                          `cursor-pointer select-none px-3 py-2 ${
                            active ? "bg-[#2B7FFF]/10 text-[#1d4ed8]" : "text-slate-700"
                          }`
                        }
                      >
                        {agent.label}
                      </Listbox.Option>
                    ))
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
      </div>
    </CallCenterModal>
  );
};

export default TicketAssignModal;
